import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useAuthStore } from "../../../context/AuthContext";
import useMedia from "../../../hooks/useMedia";
import useToast from "../../../hooks/useToast";
import { loginUser } from "../../../services/authService";
import { Button } from "../../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { FieldError, FieldGroup, FieldLabel } from "../../ui/field";
import { Input } from "../../ui/input";
import { Spinner } from "../../ui/spinner";

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export default function Login() {
  const navigate = useNavigate();
  const { getIconUrl } = useMedia();
  const { login, isAuthenticated } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (values) => {
    const response = await loginUser(values);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to sign in");
      return;
    }

    login(response.data.user, response.data.tokens);
    showSuccessToast(response.message || "Login successful");
    navigate("/dashboard");
  };

  return (
    <div className="fixed inset-0 flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(64,48,155,0.14),_transparent_34%),radial-gradient(circle_at_top_right,_rgba(240,138,0,0.14),_transparent_28%),radial-gradient(circle_at_bottom_left,_rgba(20,148,71,0.12),_transparent_30%),linear-gradient(135deg,_#f7f4fc,_#f1eff9)] p-4">
      <div className="flex w-full justify-center">
        <Card className="animate-fade-up w-full max-w-[480px] border-white/80 bg-white/84">
          <CardHeader className="px-8 pb-4 pt-8 text-center">
            <div className="mb-5 flex justify-center">
              <img
                src={getIconUrl("original-logo.jpeg")}
                alt="Sardar Patel Medical College & Research Centre"
                className="h-auto w-full max-w-[420px] rounded-2xl"
              />
            </div>
            <CardTitle className="text-3xl text-panel-ink">Sign In</CardTitle>
            <CardDescription className="text-panel-muted">
              Access the admin panel using your account credentials.
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <FieldGroup>
                <div className="space-y-2">
                  <FieldLabel>Email Address</FieldLabel>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <Input
                      className="pl-12"
                      placeholder="Enter email"
                      {...register("email")}
                    />
                  </div>
                  {errors.email ? (
                    <FieldError>{errors.email.message}</FieldError>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <FieldLabel>Password</FieldLabel>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <Input
                      className="pl-12 pr-12"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      {...register("password")}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  {errors.password ? (
                    <FieldError>{errors.password.message}</FieldError>
                  ) : null}
                </div>
              </FieldGroup>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <Spinner className="h-5 w-5 text-white" />
                    Signing In...
                  </span>
                ) : (
                  "Sign In"
                )}
              </Button>

              {/* <div className="rounded-3xl bg-slate-50 p-4 text-sm text-panel-muted">
                <p className="font-semibold text-panel-ink">Demo credentials</p>
                <p className="mt-2">Email: admin@smcrc.local</p>
                <p>Password: admin123</p>
              </div> */}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
